import React, {useEffect, useState} from 'react';
import { toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import css from './OrdersFilesComponent.module.css';
import {SliderCalendar} from "../../../ui";
import {useInitCalendarDate} from "../../../helpers";
import {ordersActions} from "../../../store/slices/orders-slices";
import {axiosService} from "../../../services";

const OrdersFilesComponent = () => {
    useInitCalendarDate(); // Initialize calendar date before render
    
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingStickers, setLoadingStickers] = useState(false);
    const [loadingTaskSheet, setLoadingTaskSheet] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    
    // Get selected date and deliveries data from Redux
    const selectedDate = useSelector(state => state.availableDishes.dataOfDay);
    const deliveriesData = useSelector(state => state.orders.deliveriesByDate);
    const { trigger } = useSelector(state => state.orders);

    // Fetch deliveries data when date changes
    useEffect(() => {
        if (selectedDate) {
            setDataLoaded(false);
            dispatch(ordersActions.getDeliveriesByDate({ date: selectedDate }))
                .then(() => {
                    // Give a small delay to ensure state is updated
                    setTimeout(() => setDataLoaded(true), 500);
                })
                .catch(() => {
                    setDataLoaded(true); // Still set to true to allow manual retry
                });
        }
    }, [selectedDate, trigger, dispatch]);

    const handlePackingFiles = async () => {
        // Show loading state
        setLoadingPdf(true);
        
        try {
            // First, ensure we have fresh data
            if (!dataLoaded) {
                toast.info(t('Loading delivery data, please wait...'));
                await dispatch(ordersActions.getDeliveriesByDate({ date: selectedDate }));
                // Wait a bit for the data to be processed
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Check if we have deliveries data after loading
            const currentDeliveries = deliveriesData;
            if (!currentDeliveries || currentDeliveries.length === 0) {
                toast.warning(t('No deliveries data available for the selected date'));
                setLoadingPdf(false);
                return;
            }

            toast.info(t('Generating PDF, please wait...'));

            // Get current language from react-i18next
            const currentLanguage = i18n.language || 'en';

            // Call backend API to generate PDF - use GET with date parameter and language query parameter
            const response = await axiosService.get(`/orders/generate-packing-pdf/${selectedDate}?language=${currentLanguage}`, {
                responseType: 'blob',
                timeout: 30000, // 30 second timeout
            });

            // Download the PDF
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `packing-files-${selectedDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success(t('PDF generated successfully!'));
        } catch (error) {
            console.error('Error generating packing files:', error);
            if (error.response?.status === 404) {
                toast.error(t('No deliveries found for the selected date'));
            } else if (error.response?.status === 500) {
                toast.error(t('Server error while generating PDF. Please try again.'));
            } else if (error.code === 'ECONNABORTED') {
                toast.error(t('Request timed out. Please try again.'));
            } else {
                toast.error(t('Error generating packing files. Please try again.'));
            }
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleStickers = async (format) => {
        // Show loading state
        setLoadingStickers(true);
        
        try {
            // First, ensure we have fresh data
            if (!dataLoaded) {
                toast.info(t('Loading delivery data, please wait...'));
                await dispatch(ordersActions.getDeliveriesByDate({ date: selectedDate }));
                // Wait a bit for the data to be processed
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Check if we have deliveries data after loading
            const currentDeliveries = deliveriesData;
            if (!currentDeliveries || currentDeliveries.length === 0) {
                toast.warning(t('No deliveries data available for the selected date'));
                setLoadingStickers(false);
                return;
            }

            toast.info(t('Generating stickers, please wait...'));

            // Get current language from react-i18next
            const currentLanguage = i18n.language || 'en';

            // Call backend API to generate stickers PDF
            const response = await axiosService.get(`/orders/generate-stickers-pdf/${selectedDate}?language=${currentLanguage}&format=${format}`, {
                responseType: 'blob',
                timeout: 300000, // 5 minutes timeout
            });

            // Detect file type from response headers
            const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
            const isZip = contentType.includes('application/zip') || contentType.includes('application/x-zip-compressed');
            const isPdf = contentType.includes('application/pdf');
            
            // Determine MIME type and file extension
            let mimeType = 'application/pdf';
            let fileExtension = 'pdf';
            let fileType = 'PDF';
            
            if (isZip) {
                mimeType = 'application/zip';
                fileExtension = 'zip';
                fileType = 'ZIP';
            } else if (isPdf) {
                mimeType = 'application/pdf';
                fileExtension = 'pdf';
                fileType = 'PDF';
            }

            // Download the file
            const blob = new Blob([response.data], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `stickers-${selectedDate}.${fileExtension}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success(t(`Stickers ${fileType} generated successfully!`));
        } catch (error) {
            console.error('Error generating stickers:', error);
            if (error.response?.status === 404) {
                toast.error(t('No deliveries found for the selected date'));
            } else if (error.response?.status === 500) {
                toast.error(t('Server error while generating stickers. Please try again.'));
            } else if (error.code === 'ECONNABORTED') {
                toast.error(t('Request timed out. Please try again.'));
            } else {
                toast.error(t('Error generating stickers. Please try again.'));
            }
        } finally {
            setLoadingStickers(false);
        }
    };

    const handleTaskSheet = async () => {
        // Show loading state
        setLoadingTaskSheet(true);
        
        try {
            // First, ensure we have fresh data
            if (!dataLoaded) {
                toast.info(t('Loading delivery data, please wait...'));
                await dispatch(ordersActions.getDeliveriesByDate({ date: selectedDate }));
                // Wait a bit for the data to be processed
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Check if we have deliveries data after loading
            const currentDeliveries = deliveriesData;
            if (!currentDeliveries || currentDeliveries.length === 0) {
                toast.warning(t('No deliveries data available for the selected date'));
                setLoadingTaskSheet(false);
                return;
            }

            toast.info(t('Generating task sheet PDF, please wait...'));

            // Get current language from react-i18next
            const currentLanguage = i18n.language || 'en';

            // Call backend API to generate task sheet PDF
            const response = await axiosService.get(`/orders/generate-task-sheet-pdf/${selectedDate}?language=${currentLanguage}`, {
                responseType: 'blob',
                timeout: 30000, // 30 second timeout
            });

            // Download the PDF
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `task-sheet-${selectedDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success(t('Task sheet PDF generated successfully!'));
        } catch (error) {
            console.error('Error generating task sheet:', error);
            if (error.response?.status === 404) {
                toast.error(t('No deliveries found for the selected date'));
            } else if (error.response?.status === 500) {
                toast.error(t('Server error while generating task sheet PDF. Please try again.'));
            } else if (error.code === 'ECONNABORTED') {
                toast.error(t('Request timed out. Please try again.'));
            } else {
                toast.error(t('Error generating task sheet. Please try again.'));
            }
        } finally {
            setLoadingTaskSheet(false);
        }
    };

    return (
        <div className={css.wrap}>
            <div className={css.blockCalendar}>
                <SliderCalendar />
            </div>
            <div className={css.blockBody}>
                <div className={css.buttonsContainer}>
                    <button 
                        className={`${css.fileButton} ${loadingPdf ? css.loading : ''}`} 
                        onClick={handlePackingFiles}
                        disabled={loadingPdf}
                    >
                        <span className={css.icon}>📦</span>
                        <div className={css.buttonContent}>
                            <h3>{t('orders.files.packing-files')}</h3>
                            <p>{t('orders.files.packing-files-desc')}</p>
                            {!dataLoaded && !loadingPdf && <small style={{color: '#666'}}>{t('Loading data...')}</small>}
                        </div>
                        {loadingPdf && (
                            <div className={css.loadingOverlay}>
                                <div className={css.spinner}></div>
                                <span className={css.loadingText}>{t('Generating...')}</span>
                            </div>
                        )}
                    </button>
                    
                    <button 
                        className={`${css.fileButton} ${loadingStickers ? css.loading : ''}`} 
                        onClick={() => handleStickers("a4")}
                        disabled={loadingStickers}
                    >
                        <span className={css.icon}>🏷️</span>
                        <div className={css.buttonContent}>
                            <h3>{t('orders.files.stickers')}</h3>
                            <p>{t('orders.files.stickers-desc')}</p>
                            {!dataLoaded && !loadingStickers && <small style={{color: '#666'}}>{t('Loading data...')}</small>}
                        </div>
                        {loadingStickers && (
                            <div className={css.loadingOverlay}>
                                <div className={css.spinner}></div>
                                <span className={css.loadingText}>{t('Generating...')}</span>
                            </div>
                        )}
                    </button>
                    
                    <button 
                        className={`${css.fileButton} ${loadingStickers ? css.loading : ''}`} 
                        onClick={() => handleStickers("60x40")}
                        disabled={loadingStickers}
                    >
                        <span className={css.icon}>🏷️</span>
                        <div className={css.buttonContent}>
                            <h3>{t('orders.files.stickers-60x40')}</h3>
                            <p>{t('orders.files.stickers-60x40-desc')}</p>
                            {!dataLoaded && !loadingStickers && <small style={{color: '#666'}}>{t('Loading data...')}</small>}
                        </div>
                        {loadingStickers && (
                            <div className={css.loadingOverlay}>
                                <div className={css.spinner}></div>
                                <span className={css.loadingText}>{t('Generating...')}</span>
                            </div>
                        )}
                    </button>
                    
                    <button 
                        className={`${css.fileButton} ${loadingStickers ? css.loading : ''}`} 
                        onClick={() => handleStickers("50x30")}
                        disabled={loadingStickers}
                    >
                        <span className={css.icon}>🏷️</span>
                        <div className={css.buttonContent}>
                            <h3>{t('orders.files.stickers-50x30')}</h3>
                            <p>{t('orders.files.stickers-50x30-desc')}</p>
                            {!dataLoaded && !loadingStickers && <small style={{color: '#666'}}>{t('Loading data...')}</small>}
                        </div>
                        {loadingStickers && (
                            <div className={css.loadingOverlay}>
                                <div className={css.spinner}></div>
                                <span className={css.loadingText}>{t('Generating...')}</span>
                            </div>
                        )}
                    </button>
                    
                    <button 
                        className={`${css.fileButton} ${loadingTaskSheet ? css.loading : ''}`} 
                        onClick={handleTaskSheet}
                        disabled={loadingTaskSheet}
                    >
                        <span className={css.icon}>📋</span>
                        <div className={css.buttonContent}>
                            <h3>{t('orders.files.task-sheet')}</h3>
                            <p>{t('orders.files.task-sheet-desc')}</p>
                            {!dataLoaded && !loadingTaskSheet && <small style={{color: '#666'}}>{t('Loading data...')}</small>}
                        </div>
                        {loadingTaskSheet && (
                            <div className={css.loadingOverlay}>
                                <div className={css.spinner}></div>
                                <span className={css.loadingText}>{t('Generating...')}</span>
                            </div>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export { OrdersFilesComponent }; 